// Centralized date utilities for consistent date formatting and management
// across the JobSchedule components

/**
 * Format date for display in MM/DD format
 * @param {string|Date} value - Date value to format
 * @returns {string} - Formatted date in MM/DD format or original value
 */
export const formatDateToMMDD = (value) => {
  if (!value) return "";
  
  if (typeof value === "string") {
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/; // Matches YYYY-MM-DD format
    if (dateRegex.test(value)) {
      const parts = value.split("-");
      return `${parts[1]}/${parts[2]}`; // Convert to MM/DD
    }
  }
  return value;
};

/**
 * Format date for HTML date input (YYYY-MM-DD format)
 * Handles various input formats including SQL datetime
 * @param {string|Date} dateString - Date string to format
 * @returns {string} - Date in YYYY-MM-DD format or empty string
 */
export const formatDateForInput = (dateString) => {
  if (!dateString) return '';
  
  try {
    // Handle SQL date format "2025-02-20 00:00:00.0000000"
    if (typeof dateString === 'string' && dateString.includes(' ')) {
      const datePart = dateString.split(' ')[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        return datePart;
      }
    }
    
    // If it's already in the correct format, return it
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    // Try to parse the date and format it correctly
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
  } catch (error) {
    console.error('Error formatting date for input:', error);
  }
  
  // Return original value if parsing fails
  return dateString;
};

/**
 * Format date without time component
 * Specifically for OrderReceived and ProductionRelease fields
 * @param {string|Date} dateString - Date string to format
 * @returns {string} - Date in YYYY-MM-DD format
 */
export const formatDateWithoutTime = (dateString) => {
  if (!dateString) return '';
  
  try {
    // If date already has time component, extract just the date part
    if (typeof dateString === 'string' && (dateString.includes(' ') || dateString.includes('T'))) {
      // Handle SQL date format "2025-02-20 00:00:00.0000000" or ISO format
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
      
      // Or just try to extract the date part if it's a string
      const datePart = dateString.split(/[ T]/)[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        return datePart;
      }
    }
    
    // If already in YYYY-MM-DD format, return as is
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }
    
    // Try to parse any other format
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
  } catch (error) {
    console.error('Error formatting date without time:', error);
  }
  
  // If all else fails, return the original string
  return dateString;
};

/**
 * Validate if a date string is in valid YYYY-MM-DD format
 * @param {string} dateString - Date string to validate
 * @returns {boolean} - True if valid format, false otherwise
 */
export const isValidDateFormat = (dateString) => {
  if (!dateString) return true;
  
  // Check if the date matches yyyy-MM-dd format
  const dateFormatRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (dateFormatRegex.test(dateString)) {
    return !isNaN(Date.parse(dateString));
  }
  
  // If it contains time component or other formats, it's not valid for the date input
  return false;
};

/**
 * Check if a date falls on a weekend (Saturday or Sunday)
 * @param {string} dateString - Date string to check
 * @returns {boolean} - True if weekend, false otherwise
 */
export const isWeekendDate = (dateString) => {
  if (!dateString) return false;
  
  try {
    // Parse the date using the local timezone to prevent timezone issues
    const parts = dateString.split(/[- :]/).map(part => parseInt(part, 10));
    
    // Create date using year, month (0-based), day
    // This ensures we're working with the date as displayed to the user
    let date;
    if (parts.length >= 3) {
      // If we have year, month, day format
      date = new Date(parts[0], parts[1]-1, parts[2]); // Month is 0-based
    } else {
      // Fallback to standard parsing
      date = new Date(dateString);
    }
    
    if (isNaN(date.getTime())) return false;
    
    const day = date.getDay();
    // 0 is Sunday, 6 is Saturday
    return day === 0 || day === 6;
  } catch (error) {
    console.error('Error checking weekend date:', error);
    return false;
  }
};

/**
 * Check if a date is outside the current year
 * @param {string} dateString - Date string to check
 * @returns {boolean} - True if outside current year, false otherwise
 */
export const isOutsideCurrentYear = (dateString) => {
  if (!dateString) return false;
  
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return false;
    const currentYear = new Date().getFullYear();
    return date.getFullYear() !== currentYear;
  } catch (error) {
    console.error('Error checking year of date:', error);
    return false;
  }
};

/**
 * Format date for display in local format
 * @param {string|Date} dateValue - Date value to format
 * @returns {string} - Formatted date string
 */
export const formatDateForDisplay = (dateValue) => {
  if (!dateValue) return "";
  
  try {
    let date;
    
    // Handle SQL server datetime format "2025-01-15 00:00:00.0000000"
    if (typeof dateValue === 'string' && dateValue.includes(' ')) {
      const datePart = dateValue.split(' ')[0];
      // Create date at midnight local time to avoid timezone issues
      const [year, month, day] = datePart.split('-');
      date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }
    // Handle ISO date format (2025-06-02)
    else if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateValue.split('-');
      date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    }
    // Fallback to standard Date parsing
    else {
      date = new Date(dateValue);
    }
    
    if (!date || isNaN(date.getTime())) return "";
    return date.toLocaleDateString();
  } catch (error) {
    console.error('Error formatting date for display:', error);
    return "";
  }
};

/**
 * Strip time component from date fields in a job object
 * @param {Object} job - Job object containing date fields
 * @returns {Object} - Job object with formatted date fields
 */
export const stripTimeFromDates = (job) => {
  const dateFields = [
    "OrderReceived",
    "ProductionRelease",
    "MechanicalDone",
    "ElectricalDone",
    "MechanicalDue",
    "ElectricalDue",
    "ControlsDue",
    "ControlsDone",
    "EngineeringDue",
    "EngineeringDone",
    "NPPipeFabDue",
    "NPPipeFabDone",
    "NPCSMDue",
    "NPCSMDone",
    "NPSuctionLineDue",
    "NPSuctionLineDone",
    "NPInducerTubeDue",
    "NPInducerTubeDone",
    "NPBMXDue",
    "NPBMXDone",
    "NPFertigationDue",
    "NPFertigationDone",
    "FabDue",
    "FabDone",
    "SoftwareDue",
    "SoftwareDone",
    "ControlPanelDue",
    "ControlPanelDone",
    "HCPumpsDue",
    "VTPumpsDue",
    "PumpDone",
    "PumpsDone",
    "AssemblyDue",
    "AssemblyDone",
    "TestDue",
    "TestDone",
    "PackDue",
    "OriginalScheduledShipDate",
    "CurrentPlanShipDate",
    "ActualShipDate",
    "ScheduledArrivalDate",
    "MechanicalLongLeadsDone",
    "ElectricalLongLeadsDone",
    "ControlPanelExpectedCompletion",
    "AssemblyExpectedCompletion",
    "TestExpectedCompletion",
    "FabExpectedCompletion",
    "PumpExpectedCompletion",
    "OrderDate",
    "RequestedDeliveryDate"
  ];

  const formattedJob = { ...job };

  dateFields.forEach((field) => {
    if (formattedJob[field]) {
      formattedJob[field] = formatDateForInput(formattedJob[field]);
    }
  });

  return formattedJob;
};

/**
 * Format all jobs dates using stripTimeFromDates
 * @param {Array} jobs - Array of job objects
 * @returns {Array} - Array of jobs with formatted dates
 */
export const formatJobDates = (jobs) => {
  return jobs.map((job) => stripTimeFromDates(job));
};

/**
 * Check if a field is a date field based on its name
 * @param {string} field - Field name to check
 * @returns {boolean} - True if it's a date field, false otherwise
 */
export const isDateField = (field) => {
  return field.includes('Date') || 
         field.includes('Due') || 
         field.includes('Done') || 
         field.includes('Completion') ||
         field === 'OrderReceived' ||
         field === 'ProductionRelease' ||
         // BookingsLog specific date fields
         field === 'CSOSubmittalSent' ||
         field === 'CSOSubmittalReceived' ||
         field === 'SignedOrderReceived' ||
         field === 'SalesmanOrderSheetReceived' ||
         field === 'EngineeringHold' ||
         field === 'LongLeads' ||
         field === 'FullRelease' ||
         field === 'DeliveryMonth';
};

/**
 * Get appropriate date formatter based on field type
 * @param {string} field - Field name
 * @param {string} value - Date value
 * @returns {string} - Formatted date value
 */
export const getFormattedDateForField = (field, value) => {
  if (!value) return "";
  
  // Special handling for OrderReceived and ProductionRelease
  if (field === 'OrderReceived' || field === 'ProductionRelease') {
    return formatDateWithoutTime(value);
  }
  
  // For other date fields, use standard formatting
  return formatDateForInput(value);
};

/**
 * UNIFIED DATE FORMATTER - Main function for consistent date formatting across the entire app
 * This function replaces all the various date formatting approaches found throughout the codebase
 * 
 * @param {string|Date} dateValue - The date value to format (can be various formats)
 * @param {string} formatType - The desired output format:
 *   - 'display' (default): For displaying dates to users (MM/DD/YYYY or localized)
 *   - 'input': For HTML date inputs (YYYY-MM-DD)
 *   - 'local': For local date display using toLocaleDateString()
 *   - 'email': For email templates (readable format)
 *   - 'short': For compact display (MM/DD)
 *   - 'iso': For API calls (YYYY-MM-DD)
 *   - 'month-year': For delivery month display (MMM YYYY, e.g., "Aug 2025")
 * @param {Object} options - Additional formatting options
 *   - fallback: What to return if date is invalid (default: '')
 *   - preserveLocal: For YYYY-MM-DD inputs, treat as local date (default: true)
 * @returns {string} - Formatted date string
 */
export const formatDateUnified = (dateValue, formatType = 'display', options = {}) => {
  const { fallback = '', preserveLocal = true } = options;
  
  // Handle null, undefined, or empty values
  if (!dateValue || dateValue === '' || dateValue === null || dateValue === undefined) {
    return fallback;
  }
  
  try {
    let date;
    
    // Handle different input formats
    if (typeof dateValue === 'string') {
      // Handle SQL Server datetime format: "2025-02-20 00:00:00.0000000"
      if (dateValue.includes(' ') && dateValue.includes(':')) {
        const datePart = dateValue.split(' ')[0];
        if (/^\d{4}-\d{2}-\d{2}$/.test(datePart) && preserveLocal) {
          const [year, month, day] = datePart.split('-');
          date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        } else {
          date = new Date(dateValue);
        }
      }
      // Handle ISO date format: "YYYY-MM-DD" - treat as local to avoid timezone issues
      else if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue) && preserveLocal) {
        const [year, month, day] = dateValue.split('-');
        date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      }
      // Handle ISO datetime format with Z suffix: "2025-02-20T00:00:00.000Z"
      else if (dateValue.includes('T') && dateValue.endsWith('Z') && preserveLocal) {
        // For dates that end with 'Z' and are at midnight UTC, treat as calendar dates
        // Extract just the date part to avoid timezone shifting
        const datePart = dateValue.split('T')[0];
        if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
          const [year, month, day] = datePart.split('-');
          date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        } else {
          date = new Date(dateValue);
        }
      }
      // Handle ISO datetime format without Z: "2025-02-20T00:00:00.000"
      else if (dateValue.includes('T')) {
        // Extract just the date part to avoid timezone shifting
        const datePart = dateValue.split('T')[0];
        if (/^\d{4}-\d{2}-\d{2}$/.test(datePart) && preserveLocal) {
          const [year, month, day] = datePart.split('-');
          date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        } else {
          date = new Date(dateValue);
        }
      }
      // Handle other string formats
      else {
        date = new Date(dateValue);
      }
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else {
      // Try to convert other types to Date
      date = new Date(dateValue);
    }
    
    // Validate the date
    if (!date || isNaN(date.getTime())) {
      console.warn(`Invalid date value: ${dateValue}`);
      return fallback;
    }
    
    // Format based on requested type
    switch (formatType) {
      case 'input':
      case 'iso':
        // For HTML date inputs and API calls - YYYY-MM-DD format
        // Use local date components to avoid timezone issues
        const isoYear = date.getFullYear();
        const isoMonth = (date.getMonth() + 1).toString().padStart(2, '0');
        const isoDay = date.getDate().toString().padStart(2, '0');
        return `${isoYear}-${isoMonth}-${isoDay}`;
        
      case 'local':
        // Use browser's locale settings
        return date.toLocaleDateString();
        
      case 'email':
        // Readable format for emails
        return date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
        
      case 'short':
        // MM/DD format for compact display
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${month}/${day}`;
        
      case 'month-year':
        // MMM YYYY format for delivery month (e.g., "Aug 2025")
        return date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short' 
        });
        
      case 'display':
      default:
        // Default display format - MM/DD/YYYY
        const displayMonth = (date.getMonth() + 1).toString().padStart(2, '0');
        const displayDay = date.getDate().toString().padStart(2, '0');
        const displayYear = date.getFullYear();
        return `${displayMonth}/${displayDay}/${displayYear}`;
    }
    
  } catch (error) {
    console.error('Error in formatDateUnified:', error, 'Input:', dateValue);
    return fallback;
  }
};

/**
 * LEGACY COMPATIBILITY WRAPPER - Maintains backward compatibility with existing formatDateForDisplay
 * Use formatDateUnified('display') for new code
 * @deprecated Use formatDateUnified instead
 */
export const formatDateForDisplayLegacy = formatDateForDisplay;

/**
 * LEGACY COMPATIBILITY WRAPPER - Maintains backward compatibility with existing formatDateForInput
 * Use formatDateUnified('input') for new code
 * @deprecated Use formatDateUnified instead
 */
export const formatDateForInputLegacy = formatDateForInput;

/**
 * Convenience functions using the unified formatter
 */

// For table displays and general UI
export const formatDateForTable = (dateValue) => formatDateUnified(dateValue, 'display');

// For form inputs
export const formatDateForForm = (dateValue) => formatDateUnified(dateValue, 'input');

// For email notifications
export const formatDateForEmail = (dateValue) => formatDateUnified(dateValue, 'email', { fallback: 'Not specified' });

// For compact displays where space is limited
export const formatDateCompact = (dateValue) => formatDateUnified(dateValue, 'short');

// For delivery month displays (MMM YYYY format)
export const formatDateMonthYear = (dateValue) => formatDateUnified(dateValue, 'month-year');

// For API calls and data storage
export const formatDateForAPI = (dateValue) => formatDateUnified(dateValue, 'iso');

/**
 * Enhanced local date formatter that handles the specific case from ReadyToSchedule.js
 * This replaces the formatDateLocal function pattern and fixes timezone issues
 */
export const formatDateLocalEnhanced = (dateValue) => {
  if (!dateValue) return "";
  
  const dateStr = dateValue.toString();
  
  // Handle YYYY-MM-DD format - treat as local date
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = dateStr.split('-');
    return new Date(year, month - 1, day).toLocaleDateString();
  }
  
  // Handle ISO datetime format with 'Z' suffix (UTC)
  // For dates at midnight UTC, treat as calendar dates in local time
  if (dateStr.includes('T') && dateStr.endsWith('Z')) {
    const datePart = dateStr.split('T')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      const [year, month, day] = datePart.split('-');
      return new Date(year, month - 1, day).toLocaleDateString();
    }
  }
  
  // Handle ISO datetime format with 'T' (e.g., "2025-11-30T00:00:00.000")
  // Extract just the date part and treat as local to avoid timezone shifting
  if (dateStr.includes('T')) {
    const datePart = dateStr.split('T')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      const [year, month, day] = datePart.split('-');
      return new Date(year, month - 1, day).toLocaleDateString();
    }
  }
  
  // Handle SQL datetime format (e.g., "2025-11-30 00:00:00.000")
  if (dateStr.includes(' ') && dateStr.includes(':')) {
    const datePart = dateStr.split(' ')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      const [year, month, day] = datePart.split('-');
      return new Date(year, month - 1, day).toLocaleDateString();
    }
  }
  
  // For other formats, use the unified formatter
  return formatDateUnified(dateValue, 'local');
};
